import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { LinkOutlined, MailOutlined, SearchOutlined, SendOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { listCustomers } from '@/api/customerApi';
import {
  createMemberMessage,
  searchMemberMessages,
  type CreateMemberMessageRequest,
  type MemberMessageItem,
  type MemberMessageSearchResult,
} from '@/api/memberMessageApi';
import { useAuth } from '@/hooks/useAuth';

const API_GUIDE_URL = '/docs/crm-member-messages.json';

type SendFormValues = Pick<
  CreateMemberMessageRequest,
  'subject' | 'content' | 'category' | 'subcategory'
>;

function createRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `crm-${crypto.randomUUID()}`;
  }
  return `crm-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function errorText(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const response = (error as { response?: { data?: { message?: string; detail?: string } } }).response;
    const serverMessage = response?.data?.message || response?.data?.detail;
    if (serverMessage) return serverMessage;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export default function MemberMessageManagementPage() {
  const { t } = useTranslation();
  const { hasPermission, loading: permissionLoading } = useAuth();
  const [form] = Form.useForm<SendFormValues>();

  const canRead = hasPermission('membermessages', 'read');
  const canWrite = hasPermission('membermessages', 'write');
  const canSearchCustomers = hasPermission('customers', 'read');

  const [recipientCustomerId, setRecipientCustomerId] = useState('');
  const [status, setStatus] = useState<'all' | 'unread' | 'read'>('all');
  const [category, setCategory] = useState('');
  const [result, setResult] = useState<MemberMessageSearchResult | null>(null);
  const [searchingMessages, setSearchingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [requestId, setRequestId] = useState(createRequestId);
  const [customerOptions, setCustomerOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  const loadMessages = useCallback(async (
    page = 1,
    pageSize = result?.pageSize || 30,
    explicitRecipient?: string
  ) => {
    const recipient = (explicitRecipient ?? recipientCustomerId).trim();
    if (!recipient) {
      message.warning(t('member_messages.select_recipient'));
      return;
    }

    setSearchingMessages(true);
    try {
      const data = await searchMemberMessages({
        recipientCustomerId: recipient,
        page,
        pageSize,
        status,
        category: category.trim() || undefined,
      });
      setResult(data);
    } catch (error) {
      message.error(errorText(error, t('member_messages.search_error')));
    } finally {
      setSearchingMessages(false);
    }
  }, [category, recipientCustomerId, result?.pageSize, status, t]);

  const searchCrmCustomers = useCallback(async (keyword: string) => {
    if (!canSearchCustomers || keyword.trim().length < 1) {
      setCustomerOptions([]);
      return;
    }

    setSearchingCustomers(true);
    try {
      const data = await listCustomers({ from: 0, size: 15, keyword: keyword.trim() });
      setCustomerOptions(data.items.map((customer) => ({
        value: customer.id,
        label: [customer.name, customer.company, customer.email, customer.id]
          .filter(Boolean)
          .join(' · '),
      })));
    } catch {
      setCustomerOptions([]);
    } finally {
      setSearchingCustomers(false);
    }
  }, [canSearchCustomers]);

  const sendMessage = useCallback(async (values: SendFormValues) => {
    const recipient = recipientCustomerId.trim();
    if (!recipient) {
      message.warning(t('member_messages.select_recipient'));
      return;
    }

    setSending(true);
    try {
      const data = await createMemberMessage({
        recipientCustomerId: recipient,
        requestId,
        subject: values.subject.trim(),
        content: values.content.trim(),
        category: values.category.trim(),
        subcategory: values.subcategory?.trim() || undefined,
      });
      message.success(data.created
        ? t('member_messages.created_success')
        : t('member_messages.existing_success'));
      form.resetFields();
      setRequestId(createRequestId());
      await loadMessages(1, result?.pageSize || 30, recipient);
    } catch (error) {
      // Keep requestId unchanged so a retry remains idempotent.
      message.error(errorText(error, t('member_messages.send_error')));
    } finally {
      setSending(false);
    }
  }, [form, loadMessages, recipientCustomerId, requestId, result?.pageSize, t]);

  const columns: ColumnsType<MemberMessageItem> = useMemo(() => [
    {
      title: t('member_messages.columns.subject'),
      dataIndex: 'subject',
      key: 'subject',
      width: 220,
      render: (value: string) => <Typography.Text strong>{value}</Typography.Text>,
    },
    {
      title: t('member_messages.columns.content'),
      dataIndex: 'content',
      key: 'content',
      render: (value: string) => (
        <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', minWidth: 260 }}>{value}</div>
      ),
    },
    {
      title: t('member_messages.columns.category'),
      dataIndex: 'category',
      key: 'category',
      width: 150,
      render: (value: string, item) => (
        <Space direction="vertical" size={2}>
          <Tag color="blue">{value || '-'}</Tag>
          {item.subcategory && <Typography.Text type="secondary">{item.subcategory}</Typography.Text>}
        </Space>
      ),
    },
    {
      title: t('member_messages.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (_value: string, item) => (
        <Tag color={item.isRead ? 'green' : 'gold'}>
          {item.isRead ? t('member_messages.status_read') : t('member_messages.status_unread')}
        </Tag>
      ),
    },
    {
      title: t('member_messages.columns.created'),
      dataIndex: 'createdAtUtc',
      key: 'createdAtUtc',
      width: 180,
      render: (value: string) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      title: t('member_messages.columns.read'),
      dataIndex: 'readAtUtc',
      key: 'readAtUtc',
      width: 180,
      render: (value: string | null) => value ? new Date(value).toLocaleString() : '-',
    },
  ], [t]);

  if (permissionLoading && !canRead) {
    return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  if (!canRead) {
    return (
      <Result
        status="403"
        title="403"
        subTitle={t('member_messages.permission_required')}
      />
    );
  }

  return (
    <div>
      <Space direction="vertical" size={4} style={{ marginBottom: 20 }}>
        <Typography.Title level={2} style={{ margin: 0 }}>
          <MailOutlined /> {t('member_messages.title')}
        </Typography.Title>
        <Typography.Text type="secondary">{t('member_messages.subtitle')}</Typography.Text>
      </Space>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 20 }}
        message={t('member_messages.company_scope')}
        description={(
          <a href={API_GUIDE_URL} target="_blank" rel="noreferrer">
            <LinkOutlined /> {t('member_messages.api_guide')}
          </a>
        )}
      />

      <Card title={t('member_messages.recipient_title')} style={{ marginBottom: 20 }}>
        <Row gutter={[16, 16]} align="bottom">
          {canSearchCustomers && (
            <Col xs={24} lg={8}>
              <Typography.Text>{t('member_messages.crm_customer_lookup')}</Typography.Text>
              <Select
                showSearch
                allowClear
                filterOption={false}
                options={customerOptions}
                loading={searchingCustomers}
                onSearch={searchCrmCustomers}
                onSelect={(value: string) => {
                  setRecipientCustomerId(value);
                  setResult(null);
                }}
                placeholder={t('member_messages.crm_customer_placeholder')}
                style={{ width: '100%', marginTop: 8 }}
              />
            </Col>
          )}
          <Col xs={24} lg={canSearchCustomers ? 7 : 12}>
            <Typography.Text>{t('member_messages.customer_id')}</Typography.Text>
            <Input
              value={recipientCustomerId}
              maxLength={128}
              onChange={(event) => {
                setRecipientCustomerId(event.target.value);
                setResult(null);
              }}
              placeholder={t('member_messages.customer_id_placeholder')}
              style={{ marginTop: 8 }}
            />
          </Col>
          <Col xs={24} sm={8} lg={3}>
            <Typography.Text>{t('member_messages.filters.status')}</Typography.Text>
            <Select
              value={status}
              onChange={setStatus}
              style={{ width: '100%', marginTop: 8 }}
              options={[
                { value: 'all', label: t('member_messages.status_all') },
                { value: 'unread', label: t('member_messages.status_unread') },
                { value: 'read', label: t('member_messages.status_read') },
              ]}
            />
          </Col>
          <Col xs={24} sm={10} lg={3}>
            <Typography.Text>{t('member_messages.filters.category')}</Typography.Text>
            <Input
              value={category}
              maxLength={64}
              onChange={(event) => setCategory(event.target.value)}
              placeholder={t('member_messages.category_placeholder')}
              style={{ marginTop: 8 }}
            />
          </Col>
          <Col xs={24} sm={6} lg={3}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              loading={searchingMessages}
              onClick={() => loadMessages(1)}
              block
            >
              {t('member_messages.search')}
            </Button>
          </Col>
        </Row>
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
          {t('member_messages.customer_id_help')}
        </Typography.Text>
      </Card>

      <Row gutter={[20, 20]} align="top">
        <Col xs={24} xl={canWrite ? 16 : 24}>
          <Card
            title={t('member_messages.results_title')}
            extra={<Statistic value={result?.total || 0} suffix={t('member_messages.total_suffix')} />}
          >
            <Table<MemberMessageItem>
              rowKey="id"
              columns={columns}
              dataSource={result?.items || []}
              loading={searchingMessages}
              scroll={{ x: 1180 }}
              locale={{ emptyText: t('member_messages.no_messages') }}
              pagination={{
                current: result?.page || 1,
                pageSize: result?.pageSize || 30,
                total: result?.total || 0,
                showSizeChanger: true,
                pageSizeOptions: [10, 30, 50, 100],
                onChange: (page, pageSize) => loadMessages(page, pageSize),
              }}
            />
          </Card>
        </Col>

        {canWrite && (
          <Col xs={24} xl={8}>
            <Card title={t('member_messages.send_title')}>
              <Alert
                type="warning"
                showIcon
                message={t('member_messages.immutable_notice')}
                style={{ marginBottom: 16 }}
              />
              <Form<SendFormValues>
                form={form}
                layout="vertical"
                initialValues={{ category: 'general' }}
                onFinish={sendMessage}
              >
                <Form.Item label={t('member_messages.request_id')}>
                  <Input value={requestId} readOnly />
                </Form.Item>
                <Form.Item
                  name="subject"
                  label={t('member_messages.subject')}
                  rules={[{ required: true, whitespace: true, max: 200 }]}
                >
                  <Input maxLength={200} placeholder={t('member_messages.subject_placeholder')} />
                </Form.Item>
                <Form.Item
                  name="content"
                  label={t('member_messages.content')}
                  rules={[{ required: true, whitespace: true, max: 4000 }]}
                >
                  <Input.TextArea
                    rows={8}
                    maxLength={4000}
                    showCount
                    placeholder={t('member_messages.content_placeholder')}
                  />
                </Form.Item>
                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      name="category"
                      label={t('member_messages.filters.category')}
                      rules={[{ required: true, whitespace: true, max: 64 }]}
                    >
                      <Input maxLength={64} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="subcategory"
                      label={t('member_messages.subcategory')}
                      rules={[{ max: 64 }]}
                    >
                      <Input maxLength={64} placeholder={t('member_messages.subcategory_placeholder')} />
                    </Form.Item>
                  </Col>
                </Row>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SendOutlined />}
                  loading={sending}
                  disabled={!recipientCustomerId.trim()}
                  block
                >
                  {t('member_messages.send')}
                </Button>
              </Form>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}
